import _typeof from "@babel/runtime/helpers/typeof";
import "core-js/modules/es6.array.find.js";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.object.get-own-property-names.js";
import "core-js/modules/es6.set.js";
import "core-js/modules/es6.string.iterator.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.array.iterator.js";
import "core-js/modules/web.dom.iterable.js";
import { redactKnownPropertyNames } from './redactKnownPropertyNames';
import { redactByRegExpPatterns } from './redactByRegExpPattern';
import { redactRequestProperty } from './redactRequestProperty';
import { isErrorLike } from './utils';

// We will iterate through each rule until we find one that's applicable.
var redactionRules = [redactRequestProperty, redactKnownPropertyNames, redactByRegExpPatterns];

/**
 * This is the non-recursive version of redact; only meant to be used internally or when the
 * caller is doing the recursion (when implementing your own stringifier for instance).
 */
export var redactWithoutRecursing = function redactWithoutRecursing(key, value) {
  var valueType = _typeof(value);
  // No need to redact booleans, numbers, undefined, or null.
  if (!value || valueType === 'number' || valueType === 'boolean') {
    return value;
  }
  var applicableRule = redactionRules.find(function (rule) {
    return rule.test(key, value);
  });
  if (applicableRule) {
    if (typeof applicableRule.replacement === 'function') {
      return applicableRule.replacement(value);
    }
    return applicableRule.replacement;
  }

  // No applicable rules for this value -> nothing to redact. Return as is.
  return value;
};

/**
 * @private 
 */
var redactRecursively = function redactRecursively(key, value, seen) {
  var valueType = _typeof(value);
  // No need to redact booleans, numbers, undefined, or null.
  if (!value || valueType === 'number' || valueType === 'boolean') {
    return value;
  }
  if (valueType === 'string') {
    return redactWithoutRecursing(key, value);
  }

  /* istanbul ignore else */
  if (valueType === 'object') {
    // Avoid circular references.
    if (seen.has(value)) {
      return '--circular-reference-omitted--';
    }
    seen.add(value);
    if (Array.isArray(value)) {
      return value.map(function (item) {
        return redactRecursively(key, item, seen);
      });
    }

    // We have an Object {} on our hands (not an array, not a primitive). We don't want to modify the original. So we'll create
    // a new one.

    // Special handling for Error like objects because we can't get keys for error like objects through for...in or Object.keys.
    // We can only grab _some_ properties using `Object.getOwnPropertyNames`.
    // the spec makes most Error object properties non-enumerable.
    // @see
    // - https://www.bennadel.com/blog/2874-error-object-properties-are-not-iterable-enumrable-in-node-js.htm
    // - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Enumerability_and_ownership_of_properties#detection_table
    // - https://github.com/nodejs/node/issues/19716#issuecomment-377763868
    //
    // Also, we use duck typing instead of instanceof to check whether something is an Error. We do that because instanceof can
    // fail in certain situations (such as when an error is created inside an iframe and handled in a parent frame).
    if (isErrorLike(value)) {
      var newError = new Error();
      var properties = Object.getOwnPropertyNames(value);
      properties.forEach(function (propertyName) {
        newError[propertyName] = redactRecursively(propertyName, value[propertyName], seen);
      });
      return newError;
    }
    var newObj = {};

    // - Remember, "in" iterates through properties of an object (and "of" through arrays).
    // - Anything that doesn't show up as an enumerable property (hello symbols and other exotica) will simply be omitted.
    // - We use for...in  instead of Object.keys because we want to go up the prototype chain and grab everything that we can. Object.keys
    //   only gives you the object's own enumerable propeties.
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (var propertyName in value) {
      newObj[propertyName] = redactRecursively(propertyName, value[propertyName], seen);
    }
    return newObj;
  }

  // This part is not expected to be executed ever, since we handle known primitives and objects before this.
  /* istanbul ignore next */
  return value;
};

/**
 * @example
 * ```js
 * // Supply the value to be redacted (this is the common use).
 * redact('redact this string');
 * redact({ creditCard: '1111 1111 1111 1111' });
 * redact(new Error('foo'));
 * 
 * // Or, a property-name AND the value to be redacted. This is useful when you are walking an object yourself.
 * // This is less commonly used.
 * redact('creditCard', '1111 1111 1111 1111');
 * redact('headers', { ...all_the_secret_headers });
 * ```
 */
export var redact = function redact() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  var key = args[0],
    value = args[1];
  if (args.length === 1) {
    key = undefined;
    value = args[0]; // eslint-disable-line prefer-destructuring
  }
  ;
  var redactedValue = redactWithoutRecursing(key, value);
  if (redactedValue !== value) {
    // In case the value was redacted by something like the request-redaction rule, then no need to dig deeper into it
    // because we expect a redaction rule to return a FULLY redacted value (this may change in the future).
    return redactedValue;
  }

  // the top level object was not picked up by any redaction rule. Time to redact recursively.
  return redactRecursively(key, value, new Set());
};